/* ── цвет ──────────────────────────────────────────────────────────── */
/** «rgb(37, 99, 235)» → «FF2563EB». Прозрачное — null. */
const toArgb = (css) => {
    const m = /^rgba?\(([^)]+)\)$/.exec(String(css).trim());
    if (!m)
        return null;
    const parts = m[1].split(',').map(s => parseFloat(s));
    const [r, g, b] = parts;
    const a = parts.length > 3 ? parts[3] : 1;
    if (!(a > 0.05))
        return null;
    const hex = (v) => Math.max(0, Math.min(255, Math.round(v)))
        .toString(16)
        .padStart(2, '0');
    return ('FF' + hex(r) + hex(g) + hex(b)).toUpperCase();
};
/**
 * Цвет фона, который виден на самом деле.
 *
 * У ячейки фон чаще прозрачный, а красит её строка, карточка или страница.
 * Поэтому поднимаемся вверх, пока не встретим непрозрачный, — иначе в
 * книге пропали бы полосатые строки и подсветка шапки.
 */
const effectiveBg = (el, stop) => {
    let node = el;
    while (node) {
        const argb = toArgb(getComputedStyle(node).backgroundColor);
        if (argb)
            return argb;
        if (node === stop)
            break;
        node = node.parentElement;
    }
    return null;
};
/* ── границы ───────────────────────────────────────────────────────── */
const borderStyle = (style, width) => {
    if (!style || style === 'none' || style === 'hidden' || width < 0.4)
        return null;
    if (style === 'double')
        return 'double';
    if (style === 'dashed')
        return 'dashed';
    if (style === 'dotted')
        return 'dotted';
    return width >= 2.5 ? 'thick' : width >= 1.5 ? 'medium' : 'thin';
};
const readBorder = (cs) => {
    const side = (w, s, c) => {
        const st = borderStyle(s, parseFloat(w) || 0);
        if (!st)
            return undefined;
        return { style: st, color: toArgb(c) || 'FF000000' };
    };
    const b = {
        top: side(cs.borderTopWidth, cs.borderTopStyle, cs.borderTopColor),
        right: side(cs.borderRightWidth, cs.borderRightStyle, cs.borderRightColor),
        bottom: side(cs.borderBottomWidth, cs.borderBottomStyle, cs.borderBottomColor),
        left: side(cs.borderLeftWidth, cs.borderLeftStyle, cs.borderLeftColor)
    };
    return b.top || b.right || b.bottom || b.left ? b : undefined;
};
/* ── шрифт ─────────────────────────────────────────────────────────── */
/** Общие семейства в имена, которые Excel действительно знает. */
const FAMILY = {
    'system-ui': 'Segoe UI',
    '-apple-system': 'Segoe UI',
    blinkmacsystemfont: 'Segoe UI',
    'sans-serif': 'Calibri',
    serif: 'Times New Roman',
    monospace: 'Consolas',
    'ui-sans-serif': 'Calibri',
    'ui-monospace': 'Consolas'
};
const fontName = (css) => {
    const first = String(css)
        .split(',')[0]
        .trim()
        .replace(/^["']|["']$/g, '');
    return FAMILY[first.toLowerCase()] || first || 'Calibri';
};
/* ── значение ячейки ───────────────────────────────────────────────── */
/**
 * Что на самом деле лежит в ячейке: число, дата или текст.
 *
 * Правила нарочно узкие. Любая вольность здесь дороже неудобства: номер
 * группы «1-23», превращённый в дату, или шифр «0012», потерявший ноль, —
 * это уже испорченные данные, а не оформление. Поэтому числом становится
 * только то, что выглядит числом целиком и не теряет при этом смысла.
 */
const cellValue = (text) => {
    const raw = text.trim();
    if (!raw)
        return { value: null };
    /* Разделитель разрядов из toLocaleString — неразрывный или узкий
       пробел; для распознавания числа он лишний. */
    const plain = raw.replace(/[  \s]/g, '');
    const percent = /^-?\d+(?:[.,]\d+)?%$/.test(plain);
    const numeric = /^-?\d+(?:[.,]\d+)?$/.test(plain);
    if (percent || numeric) {
        const body = percent ? plain.slice(0, -1) : plain;
        const digits = body.replace(/^-/, '');
        /* Ведущий ноль — признак шифра, а не числа: «0012» обязано
           остаться «0012». Длинные цепочки цифр в число тоже не переводим,
           у Excel точность пятнадцать знаков. */
        if (/^0\d/.test(digits) || digits.replace(/[.,]/g, '').length > 15)
            return { value: raw };
        const n = Number(body.replace(',', '.'));
        if (!Number.isFinite(n))
            return { value: raw };
        /* Обычным числам формат не задаём: «Общий» и так покажет 12,5 как
           12,5, причём с запятой или точкой — по языку самого Excel. Формат
           вида 0.## был бы хуже: на целом числе он рисует висящую точку. */
        if (!percent)
            return { value: n };
        return { value: n, numFmt: Number.isInteger(n) ? '0"%"' : '0.#"%"' };
    }
    /* Дата — только в одном однозначном виде ДД.ММ.ГГГГ. «1-23» и «10/11»
       остаются текстом: гадать, что имелось в виду, здесь нельзя. */
    const d = /^(\d{2})\.(\d{2})\.(\d{4})$/.exec(raw);
    if (d) {
        const day = Number(d[1]);
        const month = Number(d[2]);
        const year = Number(d[3]);
        const date = new Date(year, month - 1, day);
        if (date.getDate() === day && date.getMonth() === month - 1) {
            /* Точки в коде формата экранированы. Без этого «mm» между
               разделителями читается как минуты, и часть программ
               спотыкается на таком формате; Excel показывает 11.09.2026
               в обоих случаях, но однозначная запись надёжнее. */
            return { value: date, numFmt: 'dd\\.mm\\.yyyy' };
        }
    }
    return { value: raw };
};
/* ── разбор таблицы ────────────────────────────────────────────────── */
const ALIGN_H = {
    left: 'left',
    start: 'left',
    center: 'center',
    right: 'right',
    end: 'right'
};
const ALIGN_V = {
    top: 'top',
    middle: 'center',
    bottom: 'bottom',
    baseline: 'bottom'
};
/**
 * Поворот текста в градусах по правилам Excel.
 *
 * Вертикальная надпись задаётся либо writing-mode, либо поворотом через
 * transform; разбираем оба. В Excel 1…90 — против часовой, 91…180 — по
 * часовой (значение минус 90).
 */
const rotation = (cs) => {
    /* vertical-rl разворачивает текст по часовой стрелке на прямой угол,
       vertical-lr — против. */
    if (cs.writingMode === 'vertical-rl')
        return 180;
    if (cs.writingMode === 'vertical-lr')
        return 90;
    const m = /^matrix\(([^)]+)\)$/.exec(cs.transform);
    if (!m)
        return undefined;
    const [a, b] = m[1].split(',').map(parseFloat);
    /* В CSS положительный угол — по часовой стрелке. */
    const deg = Math.round((Math.atan2(b, a) * 180) / Math.PI);
    if (!deg)
        return undefined;
    if (deg < 0 && deg >= -90)
        return -deg; // против часовой → 1…90
    if (deg > 0 && deg <= 90)
        return 90 + deg; // по часовой → 91…180
    return undefined;
};
/**
 * Отрисованная таблица → лист книги.
 *
 * Объединения строятся по colspan и rowspan: ведём решётку занятых мест и
 * кладём каждую ячейку в первое свободное, отмечая перекрытые. Так порядок
 * столбцов не сбивается даже на многоуровневой шапке, где верхняя строка
 * короче нижней.
 */
export const readTable = (table, options) => {
    var _a;
    const { title, meta = [], sheetName } = options;
    /* Шапка документа над таблицей: без года, полугодия и отбора
       выгруженный файл через неделю уже не опознать. */
    const lead = [];
    if (title) {
        lead.push([{ value: title, style: { font: { bold: true, size: 13 }, align: { v: 'center' } } }]);
    }
    meta.filter(Boolean).forEach(m => lead.push([{ value: m, style: { font: { size: 10, color: 'FF595959' } } }]));
    if (lead.length)
        lead.push([]);
    const offset = lead.length;
    const grid = [];
    const merges = [];
    const busy = [];
    const rowHeights = [];
    /** Ширина каждого столбца в точках — самая широкая из его ячеек. */
    const colPx = [];
    /** Место занято ячейкой, растянутой сверху или слева. */
    const taken = (r, c) => { var _a; return !!((_a = busy[r]) === null || _a === void 0 ? void 0 : _a[c]); };
    const take = (r, c) => {
        if (!busy[r])
            busy[r] = [];
        busy[r][c] = true;
    };
    const rows = Array.from(table.rows).filter(tr => getComputedStyle(tr).display !== 'none');
    const headRows = Array.from(((_a = table.tHead) === null || _a === void 0 ? void 0 : _a.rows) || []).length;
    rows.forEach((tr, r) => {
        var _a, _b;
        if (!grid[r])
            grid[r] = [];
        rowHeights[r] = tr.offsetHeight * 0.75;
        let c = 0;
        for (const el of Array.from(tr.cells)) {
            const cs = getComputedStyle(el);
            if (cs.display === 'none')
                continue;
            while (taken(r, c))
                c++;
            const colSpan = Math.max(1, el.colSpan);
            const rowSpan = Math.max(1, el.rowSpan);
            const bg = effectiveBg(el, table);
            const weight = parseInt(cs.fontWeight, 10);
            const decoration = cs.textDecorationLine || cs.textDecoration || '';
            const style = {
                font: {
                    name: fontName(cs.fontFamily),
                    size: Math.round(parseFloat(cs.fontSize) * 0.75 * 10) / 10,
                    bold: (Number.isFinite(weight) && weight >= 600) || cs.fontWeight === 'bold',
                    italic: cs.fontStyle === 'italic' || cs.fontStyle === 'oblique',
                    underline: decoration.includes('underline'),
                    strike: decoration.includes('line-through'),
                    color: toArgb(cs.color) || undefined
                },
                fill: bg || undefined,
                border: readBorder(cs),
                align: {
                    h: ALIGN_H[cs.textAlign] || undefined,
                    v: ALIGN_V[cs.verticalAlign] || 'center',
                    /* nowrap на экране держит колонку узкой; в книге это
                       означает «не переносить», ширину подберём по тексту. */
                    wrap: !/nowrap|pre$/.test(cs.whiteSpace),
                    rotate: rotation(cs)
                }
            };
            const { value, numFmt } = cellValue((_b = (_a = el.innerText) !== null && _a !== void 0 ? _a : el.textContent) !== null && _b !== void 0 ? _b : '');
            if (numFmt)
                style.numFmt = numFmt;
            grid[r][c] = { value, style };
            /* Ширина ячейки, растянутой на несколько столбцов, делится
               между ними поровну: иначе один столбец забрал бы всю. */
            const share = el.offsetWidth / colSpan;
            for (let dc = 0; dc < colSpan; dc++)
                colPx[c + dc] = Math.max(colPx[c + dc] || 0, share);
            /* Места под объединением занимаем пустыми ячейками того же
               оформления: иначе в книге у объединения пропадут рамки. */
            for (let dr = 0; dr < rowSpan; dr++) {
                for (let dc = 0; dc < colSpan; dc++) {
                    take(r + dr, c + dc);
                    if (dr || dc) {
                        if (!grid[r + dr])
                            grid[r + dr] = [];
                        grid[r + dr][c + dc] = { value: null, style };
                    }
                }
            }
            if (colSpan > 1 || rowSpan > 1) {
                merges.push({ r1: r + offset, c1: c, r2: r + rowSpan - 1 + offset, c2: c + colSpan - 1 });
            }
            c += colSpan;
        }
    });
    /* Единица ширины в Excel — знак шрифта по умолчанию, это около семи
       точек. Границы снизу и сверху держат крайности: столбец в один знак
       нечитаем, а в сто — разваливает лист. */
    const width = grid.reduce((m, r) => Math.max(m, r.length), 0);
    const cols = [];
    for (let c = 0; c < width; c++)
        cols[c] = Math.max(6, Math.min(70, (colPx[c] || 70) / 7));
    /* Строки шапки документа растягиваем на всю ширину — иначе длинный
       заголовок обрежется по первой колонке. */
    lead.forEach((row, i) => {
        if (row.length)
            merges.push({ r1: i, c1: 0, r2: i, c2: Math.max(0, width - 1) });
    });
    const body = grid.map(row => {
        const out = [];
        for (let c = 0; c < width; c++)
            out[c] = row[c] || { value: null };
        return out;
    });
    return {
        name: sheetName,
        rows: [...lead, ...body],
        merges,
        cols,
        rowHeights: [...lead.map(() => 18), ...rowHeights],
        freezeRows: offset + headRows
    };
};
